import React from "react";
import { useNavigate } from "react-router-dom";
import { X, LogIn, PawPrint } from "lucide-react";
import { track } from "../lib/telemetry";

/**
 * Contextual sign-in nudge. Shown when a logged-out walker taps a
 * high-value action (save / export GPX). Friendly, dismissible, and
 * can optionally offer a "continue as guest" path for the soft gates
 * (e.g. favourites that anon-cache in localStorage).
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   title      string
 *   body       string
 *   reason     string (telemetry tag, e.g. "save_walk" | "export_gpx")
 *   guestLabel optional. When provided, renders a secondary CTA that
 *              calls onGuest() — letting the user continue without an
 *              account (used by the save-walk soft gate).
 *   onGuest    () => void
 */
export default function AuthGateModal({
  open,
  onClose,
  title,
  body,
  reason,
  guestLabel,
  onGuest,
}) {
  const navigate = useNavigate();
  if (!open) return null;

  const onSignIn = () => {
    track("auth_gate_signin_click", { reason });
    onClose?.();
    navigate("/auth");
  };

  const onGuestClick = () => {
    track("auth_gate_guest_click", { reason });
    onGuest?.();
    onClose?.();
  };

  const onDismiss = () => {
    track("auth_gate_dismissed", { reason });
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1300] bg-brand-text/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onDismiss}
      data-testid="auth-gate-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-brand-surface w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 animate-fade-up"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="w-11 h-11 rounded-2xl bg-brand-accent/15 text-brand-accent flex items-center justify-center shrink-0">
            <PawPrint size={20} />
          </div>
          <button
            data-testid="auth-gate-close"
            onClick={onDismiss}
            className="text-brand-subtle"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <h3 className="font-heading text-2xl text-brand-text mt-3 leading-tight">{title}</h3>
        <p className="text-sm text-brand-subtle mt-2 leading-relaxed">{body}</p>

        <button
          data-testid="auth-gate-signin"
          type="button"
          onClick={onSignIn}
          className="td-btn-primary w-full mt-5"
        >
          <LogIn size={15} /> Create a free account
        </button>

        {guestLabel && (
          <button
            data-testid="auth-gate-guest"
            type="button"
            onClick={onGuestClick}
            className="w-full mt-2 py-2.5 text-sm text-brand-subtle hover:text-brand-text"
          >
            {guestLabel}
          </button>
        )}
      </div>
    </div>
  );
}
